// Cold-load auth bootstrap (PRD §9.4 + docs/frontend_architecture.md §6,
// B1e PR4).
//
// The access token lives in memory only, so a hard refresh — or any
// fresh tab on a still-authenticated session — starts with a null
// token. The httpOnly refresh-token cookie is durable, though, so we
// can swap it for a fresh access token at app boot.
//
// `attemptBootstrap` runs at most once per page load:
//   - First call: kicks off `refresh()` + `/me`, populating the store
//     on success.
//   - Concurrent callers (e.g. burst of route loads during boot) await
//     the same in-flight promise.
//   - Subsequent calls after settle: no-op.
//
// Failures are silent on purpose: the route guard reads the store
// after the await and redirects to /login when it's still empty. The
// 401 interceptor in client.ts is the one that handles per-request
// refreshes after bootstrap.

import { useAuthStore } from '../stores/authStore';
import { fetchMe, refresh } from './auth';

let inflight: Promise<void> | null = null;
let attempted = false;

export function attemptBootstrap(): Promise<void> {
  if (attempted) return Promise.resolve();
  if (inflight) return inflight;
  const p = doBootstrap()
    .catch(() => {
      // Refresh cookie is missing or rejected. Leaving the store empty
      // is the correct signal — the route guard will redirect.
    })
    .finally(() => {
      attempted = true;
      if (inflight === p) inflight = null;
    });
  inflight = p;
  return p;
}

async function doBootstrap(): Promise<void> {
  await refresh();
  const me = await fetchMe();
  useAuthStore.getState().setUser(me);
}

// Test-only seam. Production code should never call this — bootstrap
// is meant to run exactly once per app load.
export function _resetBootstrapForTests(): void {
  attempted = false;
  inflight = null;
}
